const MIB = 1024 * 1024;

export enum FileViolation {
  TooBig = "TOO_BIG",
  WrongType = "WRONG_TYPE",
}

export interface FileConstraints {
  maxSize?: number;
  acceptedTypes?: readonly string[];
}

export interface PickedFileFacts {
  byteSize: number;
  mimeType?: string;
  extension?: string;
}

const LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "image/jpeg": "JPG",
  "image/jpg": "JPG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/bmp": "BMP",
  "image/heic": "HEIC",
  "image/heif": "HEIC",
  "image/webp": "WEBP",
};

/** The same format under its several spellings. */
const ALIASES: Record<string, string> = {
  JPEG: "JPG",
  HEIF: "HEIC",
};

/**
 * The form definition names its types either as mime types or as bare extensions, and the back
 * office sends values this app has never heard of. Everything is reduced to the extension, which
 * is the part a reader recognises and the part both spellings share.
 */
export function normalizeType(type: string): string {
  const known = LABELS[type.toLowerCase()];
  if (known) {
    return known;
  }
  const subtype = (type.includes("/") ? type.slice(type.lastIndexOf("/") + 1) : type).toUpperCase();
  return ALIASES[subtype] ?? subtype;
}

export function acceptedTypeLabels(acceptedTypes: readonly string[] | undefined): string[] {
  if (!acceptedTypes?.length) {
    return [];
  }
  return acceptedTypes.reduce<string[]>((labels, type) => {
    const label = normalizeType(type);
    return labels.includes(label) ? labels : [...labels, label];
  }, []);
}

export function formatMaxSize(maxSize: number): string {
  const inMib = maxSize / MIB;
  return `${Number.isInteger(inMib) ? inMib : inMib.toFixed(1)} Mo`;
}

/**
 * Le poids réel du fichier derrière une chaîne base64, qui en pèse 4/3. Confondre les deux fait
 * afficher un tiers de trop et refuser des fichiers qui tenaient.
 */
export function base64ByteSize(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

export function checkFile(facts: PickedFileFacts, constraints: FileConstraints): FileViolation[] {
  const violations: FileViolation[] = [];

  if (constraints.maxSize !== undefined && facts.byteSize > constraints.maxSize) {
    violations.push(FileViolation.TooBig);
  }

  const expected = acceptedTypeLabels(constraints.acceptedTypes);
  if (expected.length) {
    const actual = [facts.mimeType, facts.extension].filter(Boolean).map((value) => normalizeType(value as string));
    // A file whose own type could not be read is left alone: a wrong guess would block a document
    // the back office would have taken.
    if (actual.length && !actual.some((type) => expected.includes(type))) {
      violations.push(FileViolation.WrongType);
    }
  }

  return violations;
}
